import axios from 'axios';
import mock from './_mock';
import errorHandler from './_errorHandler';
import { ERROR_CODES, HTTP_METHOD, CODE_TIMEOUT } from './_const';
import { openLoading, closeLoading } from '../utils/loading';

const MOCK_DELAY_TIME = 1500;
const MOCK_ENABLED = process.env.REACT_APP_USE_MOCK === 'true';

export default class Axios {
  constructor({
    baseURL = '',
    token = '',
    timeout = 0,
    recallOn = false,
    recallTimes = 0,
    dialogHandler,
    headers = {},
    shouldReturnCode = true,
  }) {
    this._axios = axios.create();
    this._baseURL = baseURL || '';
    this._timeout = timeout ? timeout * 1000 : 0;
    this._token = token || '';
    this._recallOn = recallOn || false;
    this._recallTimes = recallTimes || 0;
    this._dialogHandler = dialogHandler;
    this._headers = headers;
    this._shouldReturnCode = shouldReturnCode;

    this._init();
  }

  _init() {
    this._axios = axios.create({
      baseURL: this._baseURL,
      timeout: this._timeout,
      headers: Object.assign(
        {},
        {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this._token}`,
        },
        this._headers
      ),
    });
  }

  async _api(
    apiMethod,
    url,
    parameters = {},
    { noDialog } = { noDialog: false }
  ) {
    if (MOCK_ENABLED)
      return new Promise((resolve) => {
        openLoading();
        setTimeout(() => {
          resolve(mock[url]);
          closeLoading();
        }, MOCK_DELAY_TIME);
      });

    const isGet = apiMethod === HTTP_METHOD.GET;
    const isPost = apiMethod === HTTP_METHOD.POST;
    const isPut = apiMethod === HTTP_METHOD.PUT;
    const isDelete = apiMethod === HTTP_METHOD.DELETE;
    if (!isGet && !isPost && !isPut && !isDelete) {
      throw new TypeError('error: api method invalid');
    }

    let response;
    let requestTimes = 0;
    const requestTimesLimit = this._recallOn ? this._recallTimes : 0;

    let callAxios;
    let hadCalled;
    let isError = false;

    while (
      ((!response || response.code === CODE_TIMEOUT) &&
        requestTimes < requestTimesLimit) ||
      (!this._recallOn && !hadCalled)
    ) {
      callAxios = isGet
        ? this._axios.get(url, parameters)
        : isPost
        ? this._axios.post(url, parameters)
        : isPut
        ? this._axios.put(url, parameters)
        : isDelete
        ? this._axios.delete(url, parameters)
        : undefined;

      openLoading();

      response = await callAxios
        .then((axiosRespose) => {
          if (
            !axiosRespose.data ||
            (this._shouldReturnCode && !axiosRespose.data.code) ||
            ERROR_CODES.includes(axiosRespose.data.code)
          )
            throw axiosRespose;

          isError = false;
          return axiosRespose.data;
        })
        .catch((error) => {
          isError = true;
          return errorHandler({
            error,
            requestTimes,
            requestTimesLimit,
            dialogHandler: this._dialogHandler,
            shouldReturnCode: this._shouldReturnCode,
            noDialog,
          });
        });

      requestTimes++;
      closeLoading();
      hadCalled = true;
    }

    if (isError) throw response;
    return response;
  }

  /**
   * Get data from server
   * @param url target path
   * @param params request params
   */
  get(url, parameters, options) {
    return this._api(HTTP_METHOD.GET, url, { params: parameters }, options);
  }

  /**
   * Post data to server
   * @param url target path
   * @param params request params
   * @param recallOn enable recall (default: true)
   * @param recallTimes times of recall (default: 3)
   */
  post(url, parameters, options) {
    return this._api(HTTP_METHOD.POST, url, parameters, options);
  }

  /**
   * Send data to server by Put
   * @param url target path
   * @param params request params
   * @param recallOn enable recall (default: true)
   * @param recallTimes times of recall (default: 3)
   */
  put(url, parameters, options) {
    return this._api(HTTP_METHOD.PUT, url, parameters, options);
  }

  /**
   * Send data to server by Delete
   * @param url target path
   * @param params request params
   * @param recallOn enable recall (default: true)
   * @param recallTimes times of recall (default: 3)
   */
  delete(url, parameters, options) {
    return this._api(HTTP_METHOD.DELETE, url, parameters, options);
  }

  /**
   * Update the token of API instance
   * @param token the new token to update
   */
  updateToken(token) {
    this._token = token;
    this._init();
  }
}
